/**
 * RepBuddy - Company Enrichment Service
 * Calls backend POST /api/enrich/companies
 */

import { supabase } from '../lib/supabaseClient';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

async function authedHeaders() {
  const base = { 'Content-Type': 'application/json' };
  if (!supabase) return base;
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) return base;
  return { ...base, Authorization: `Bearer ${session.access_token}` };
}

/**
 * Resolve company names to domains using KNOWN_DOMAINS + Google Custom Search.
 * @param {string[]} companies   - company name strings
 * @param {string}   icpIndustry - optional industry hint to narrow Google search
 * @returns {{ resolved: object[], ambiguous: object[] }}
 */
// `opts.candidates` asks for every candidate the resolver considered on each resolved row, not
// only the ambiguous ones; the chat's pre-search card shows them so the user picks the company.
export async function resolveDomains(companies, icpIndustry = '', { candidates = false } = {}) {
  const r = await fetch(`${BACKEND_URL}/api/enrich/resolve-domains`, {
    method:  'POST',
    headers: await authedHeaders(),
    body:    JSON.stringify({ companies, icpIndustry, ...(candidates ? { candidates: true } : {}) }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({ error: 'Domain resolution failed' }));
    throw new Error(err.error || `Resolution failed with status ${r.status}`);
  }
  return r.json(); // { resolved: [{name,domain,source}], ambiguous: [{name,candidates}] }
}

/**
 * STANDALONE website-lookup workflow: resolve correct websites for company names.
 * Unlike resolveDomains above (the free, bundled pre-step inside enrichment), this passes
 * meter:true so the backend charges 1 website-lookup credit per row actually resolved.
 * Surfaces { blocked, usage } on a 402 so the chat can show an honest allowance message.
 */
export async function resolveWebsites(companies) {
  const r = await fetch(`${BACKEND_URL}/api/enrich/resolve-domains`, {
    method:  'POST',
    headers: await authedHeaders(),
    body:    JSON.stringify({ companies, meter: true }),
  });
  if (r.status === 402) {
    const d = await r.json().catch(() => ({}));
    return { blocked: true, reason: d.error || 'out_of_lookups', usage: d.usage || null };
  }
  if (!r.ok) {
    const err = await r.json().catch(() => ({ error: 'Website lookup failed' }));
    throw new Error(err.error || `Website lookup failed with status ${r.status}`);
  }
  return r.json(); // { resolved: [{name,domain,source}], ambiguous: [{name,candidates}], usage? }
}

// ─── SSE stream helper ────────────────────────────────────────────────────────

async function readSSEStream(response, onEvent) {
  const reader  = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer    = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';
    for (const line of lines) {
      if (line.startsWith('data: ')) {
        try { onEvent(JSON.parse(line.slice(6))); } catch {}
      }
    }
  }
}

/**
 * Stream variant of enrichCompanies - calls /api/enrich/companies/stream.
 * Invokes onEvent for each SSE event: start, progress, done, error.
 * Returns { results, summary } from the final 'done' event.
 */
export async function enrichCompaniesStream(companies, fields, resolvedDomains = {}, onEvent = () => {}) {
  const response = await fetch(`${BACKEND_URL}/api/enrich/companies/stream`, {
    method:  'POST',
    headers: await authedHeaders(),
    body:    JSON.stringify({ companies, fields, resolvedDomains }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Stream request failed' }));
    throw new Error(err.error || `Stream failed with status ${response.status}`);
  }

  return new Promise((resolve, reject) => {
    let finalResult = null;
    let blocked = null; // { reason, usage } when the enrichment allowance is exhausted

    readSSEStream(response, (event) => {
      onEvent(event);
      if (event.type === 'blocked') {
        // Out of company enrichments - remember it so the caller can show an honest message.
        blocked = { reason: event.reason || 'out_of_enrichments', usage: event.usage || null };
      } else if (event.type === 'done') {
        // `usage` is the live company_enrichments meter after this run (allowance/used/remaining).
        finalResult = { results: event.results || [], summary: event.summary || {}, usage: event.usage || null, blocked };
      } else if (event.type === 'error') {
        reject(new Error(event.message || 'Enrichment failed'));
      }
    }).then(() => {
      if (finalResult) resolve(finalResult);
      else reject(new Error('Stream ended without result'));
    }).catch(reject);
  });
}

/**
 * Enrich an array of company names with the requested fields.
 * @param {string[]} companies        - list of company name strings (max 100)
 * @param {string[]} fields           - subset of AVAILABLE_FIELDS keys
 * @param {object}   resolvedDomains  - optional map of {normalisedName: domain} from resolve-domains
 * @returns {{ results: object[], summary: object }}
 */
export async function enrichCompanies(companies, fields, resolvedDomains = {}) {
  const r = await fetch(`${BACKEND_URL}/api/enrich/companies`, {
    method:  'POST',
    headers: await authedHeaders(),
    body:    JSON.stringify({ companies, fields, resolvedDomains }),
  });

  if (!r.ok) {
    const err = await r.json().catch(() => ({ error: 'Enrichment request failed' }));
    throw new Error(err.error || `Enrichment failed with status ${r.status}`);
  }

  return r.json(); // { results: [...], summary: { enriched, partial, notFound } }
}

// Field metadata - label, which API provides it, whether it needs LinkdAPI credits
export const AVAILABLE_FIELDS = [
  { key: 'employees',       label: 'Head Count',            source: 'RepBuddy',            usesLinkdAPI: false },
  { key: 'revenue',         label: 'Annual Revenue',        source: 'RepBuddy',            usesLinkdAPI: false },
  { key: 'industry',        label: 'Industry',              source: 'RepBuddy + LinkedIn', usesLinkdAPI: false },
  { key: 'website',         label: 'Website',               source: 'RepBuddy + LinkedIn', usesLinkdAPI: false },
  { key: 'hqLocation',      label: 'HQ City / Location',    source: 'RepBuddy + LinkedIn', usesLinkdAPI: false },
  { key: 'foundedYear',     label: 'Founded Year',          source: 'LinkedIn',           usesLinkdAPI: true  },
  { key: 'operatingStatus', label: 'Operating Status',      source: 'LinkedIn',           usesLinkdAPI: true  },
  { key: 'description',     label: 'Company Description',   source: 'LinkedIn',           usesLinkdAPI: true  },
  { key: 'specialties',     label: 'Specialties',           source: 'LinkedIn',           usesLinkdAPI: true  },
  { key: 'linkedinUrl',     label: 'LinkedIn Company URL',  source: 'LinkedIn',           usesLinkdAPI: true  },
];

// CSV column header for each field key
export const FIELD_CSV_HEADERS = {
  employees:       'Head Count',
  revenue:         'Annual Revenue',
  industry:        'Industry',
  website:         'Website',
  hqLocation:      'HQ City / Location',
  foundedYear:     'Founded Year',
  operatingStatus: 'Operating Status',
  description:     'Company Description',
  specialties:     'Specialties',
  linkedinUrl:     'LinkedIn URL',
};
